/**
 * StatusBar.ts — Persistent top-line status display for Gallowcrest.
 *
 * Shows: Class | HP | Level | Chapter | Location
 * Adapted from Ascend's StatusBar (which shows Gold/Men/Food/Turn/Season/Year).
 */

import type { Terminal } from './Terminal';
import { theme, type ColorName } from './ColorTheme';

export interface StatusBarData {
  className?: string;
  hp?: number;
  hpMax?: number;
  level?: number;
  chapter?: string;
  location?: string;
  frostbite?: number;
  corruption?: number;
  dominantTrait?: string;
}

interface StatusItem {
  label: string;
  value: string;
  color: ColorName;
}

export class StatusBar {
  private terminal: Terminal;
  private data: StatusBarData = {};
  private visible = false;

  constructor(terminal: Terminal) {
    this.terminal = terminal;
  }

  /** Update the status bar data and re-render */
  update(data: Partial<StatusBarData>): void {
    this.data = { ...this.data, ...data };
    if (this.visible) {
      this.render();
    }
  }

  /** Show the status bar */
  show(): void {
    this.visible = true;
    this.render();
  }

  /** Hide the status bar */
  hide(): void {
    this.visible = false;
  }

  /** Check if visible */
  isVisible(): boolean {
    return this.visible;
  }

  /** Render the status bar at the top of the terminal */
  render(): void {
    if (!this.visible) return;

    const items: StatusItem[] = [];

    if (this.data.className) {
      items.push({
        label: '',
        value: this.data.className,
        color: 'magenta',
      });
    }

    if (this.data.hp !== undefined && this.data.hpMax !== undefined) {
      const hpRatio = this.data.hp / this.data.hpMax;
      const hpColor: ColorName = hpRatio > 0.5 ? 'green' : hpRatio > 0.25 ? 'yellow' : 'red';
      items.push({
        label: 'HP',
        value: `${this.data.hp}/${this.data.hpMax}`,
        color: hpColor,
      });
    }

    if (this.data.level !== undefined) {
      items.push({
        label: 'Lv',
        value: String(this.data.level),
        color: 'frostSilver',
      });
    }

    if (this.data.chapter) {
      items.push({
        label: 'Ch',
        value: this.data.chapter,
        color: 'yellow',
      });
    }

    if (this.data.frostbite !== undefined && this.data.frostbite > 0) {
      const fbColor: ColorName = this.data.frostbite >= 75 ? 'red' : this.data.frostbite >= 40 ? 'yellow' : 'frostbiteCyan';
      items.push({
        label: 'FROST',
        value: `${this.data.frostbite}/100`,
        color: fbColor,
      });
    }

    if (this.data.corruption !== undefined && this.data.corruption > 0) {
      const crColor: ColorName = this.data.corruption >= 75 ? 'red' : this.data.corruption >= 40 ? 'yellow' : 'magenta';
      items.push({
        label: 'CORR',
        value: `${this.data.corruption}/100`,
        color: crColor,
      });
    }

    if (this.data.location) {
      items.push({
        label: '',
        value: this.data.location,
        color: 'blue',
      });
    }

    if (this.data.dominantTrait) {
      items.push({
        label: '',
        value: `[${this.data.dominantTrait}]`,
        color: 'frostSilver',
      });
    }

    // Build the status line
    const parts = items.map(item => {
      const label = item.label ? `${item.label} ` : '';
      return theme.colorize(`${label}${item.value}`, item.color);
    });

    const line = `  ${parts.join(theme.colorize(' | ', 'gray'))}`;

    // Save cursor, render at row 1, restore cursor
    this.terminal.writeRaw('\x1b[s');
    this.terminal.setCursor(1, 1);
    this.terminal.writeRaw('\x1b[2K');
    this.terminal.writeRaw(line);
    this.terminal.setCursor(2, 1);
    this.terminal.writeRaw('\x1b[2K');
    const { cols } = this.terminal.getSize();
    this.terminal.write('─'.repeat(cols), 'gray');
    this.terminal.writeRaw('\x1b[u');
  }
}
